import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaRobot, 
  FaLightbulb, 
  FaUserFriends, 
  FaChartLine, 
  FaLeaf, 
  FaChild, 
  FaSearch, 
  FaGraduationCap, 
  FaShieldAlt, 
  FaSync,
  FaBan,
  FaCheckCircle,
  FaBuilding,
  FaHandshake,
  FaBullhorn,
  FaLock,
  FaExclamationTriangle,
  FaFileAlt,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import style from './AIPolicyPage.module.css';

const AIPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className={style.pageContainer}>
      <header className={style.header}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={style.headerContent}
        >
          <div className={style.badge}>
            <FaRobot className={style.badgeIcon} />
            <span>TALENT14 GÜVENLİ YAPAY ZEKA</span>
          </div>
          <h1 className={style.title}>📊 TALENT14 Yapay Zekâ Politika Belgesi</h1>
          <p className={style.subtitle}>
            Yapay zekâ, eğitim dünyasında hızla yaygınlaşan güçlü bir teknolojidir. Ancak bu gücün nasıl, hangi sınırlar içinde ve kimin sorumluluğunda kullanıldığı; teknolojinin kendisinden çok daha belirleyicidir.
          </p>
        </motion.div>
      </header>

      <main className={style.mainContent}>
        <motion.section 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className={style.introSection}
        >
          <div className={style.introGrid}>
            <div className={style.introText}>
              <motion.p variants={itemVariants}>
                Talent14, yapay zekâyı yalnızca bir teknoloji yatırımı olarak değil; eğitimde etik, pedagojik ve kurumsal bir sorumluluk alanı olarak ele alır.
              </motion.p>
              <motion.p variants={itemVariants}>
                Bu politika belgesi; Talent14’ün yapay zekâya bakışını, kullanım sınırlarını, insan–kurum–teknoloji dengesini ve kurumlara verdiği açık taahhütleri ortaya koymak amacıyla hazırlanmıştır.
              </motion.p>
            </div>
            
            <motion.div variants={itemVariants} className={style.goalCard}>
              <div className={style.goalIcon}>🎯</div>
              <h3>Hedeflenen Çerçeve</h3>
              <ul>
                <li><FaCheckCircle /> Yapay zekânın eğitimde kontrolsüz ve amaçsız kullanımını önlemek</li>
                <li><FaCheckCircle /> Öğretmenin pedagojik rolünü korumak</li>
                <li><FaCheckCircle /> Öğrencinin yaşına ve gelişimine uygun, güvenli ve etik bir öğrenme ortamı sunmak</li>
                <li><FaCheckCircle /> Kurumlara şeffaf, denetlenebilir ve sürdürülebilir bir yapay zekâ çerçevesi sağlamaktır.</li>
              </ul>
            </motion.div>
          </div>
          
          <motion.div 
            variants={itemVariants} 
            className={style.visionBox}
            whileHover={{ scale: 1.02 }}
          >
            <p>Talent14 için yapay zekâ; öğretmenin yerini alan bir sistem değil, öğretmeni ve öğrenciyi güçlendiren bilinçli bir araçtır.</p>
          </motion.div>
        </motion.section>

        <section className={style.section}>
          <div className={style.sectionHeader}>
            <h2 className={style.sectionTitle}>🧭 1. Vizyon ve Kurumsal Duruş İlkeleri</h2>
            <p className={style.sectionIntroText}>
              Talent14’ün yapay zekâya bakışını tanımlayan dokuz temel ilke; pedagojik, etik ve kurumsal vizyonumuzu temsil eder.
            </p>
          </div>
          
          <div className={style.principlesGrid}>
            <PrincipleCard 
              icon={<FaLightbulb />} 
              tag="1.1" 
              title="Bilinçli Araç Kullanımı"
              text="Talent14, yapay zekâyı kendi başına bir hedef olarak değil; doğru öğrenme hedeflerine hizmet eden bilinçli bir araç olarak konumlandırır. Yapay zekâ yalnızca ihtiyaç duyulduğunda devreye girer, gereksiz ve tekrar eden kullanımı teşvik etmez. Bu yaklaşım, öğrencilerin yapay zekâya bağımlı hâle gelmesini önlerken, öğrenme süreçlerinin niteliğini artırır."
              frameworks={["OECD AI Policy Framework", "NIST AI Risk Management Framework (RMF)", "Ulusal Yapay Zekâ Stratejisi (Risk temelli yönetişim)"]}
              description="Nicelik yerine öğrenme etkisini esas alan yaklaşım, NIST AI RMF’de tanımlanan amaç–risk–çıktı dengesine dayanmaktadır."
            />
            <PrincipleCard 
              icon={<FaChartLine />} 
              tag="1.2" 
              title="Üretim Odaklı Yetkinlik"
              text="Talent14, yapay zekâ kullanımını yalnızca tüketilen veya hazır çıktılarla sınırlı bir deneyim olarak ele almaz. Yapay zekâ; öğrencilerin üretmeyi öğrenmesi, kodlama ve yazılım mantığını kavraması ve teknolojinin nasıl çalıştığını anlaması için yapılandırılmış bir öğrenme aracıdır. Bu yaklaşımda amaç; yapay zekâ ile iş yapan değil, yapay zekâyı anlayan, sorgulayan ve üretebilen bireyler yetiştirmektir. Yapay zekâ; algoritmik düşünme, problem çözme ve yazılım temelli üretimi destekleyen bir öğrenme ortağı olarak konumlandırılır."
              frameworks={["UNESCO – AI Competency Framework & AI Literacy", "OECD – Skills for the Future & Computational Thinking", "MEB Dijital Yetkinlik ve Kodlama Yaklaşımları", "Ulusal Yapay Zekâ Stratejisi (Yetkin insan kaynağı ve üretim odağı)"]}
              description="Bu ilke; yapay zekâ okuryazarlığını kullanımın ötesine taşıyarak üretim ve yetkinlik boyutuna odaklanan ulusal ve uluslararası stratejik yaklaşımlarla uyumludur."
            />
            <PrincipleCard 
              icon={<FaUserFriends />} 
              tag="1.3" 
              title="İnsan Merkezlilik"
              text="Eğitimin merkezinde her zaman insan vardır. Öğretmen pedagojik liderdir; öğrenme hedeflerini belirler ve süreci yönetir. Öğrenci ise aktif öğrenendir. Yapay zekâ, bu iki unsurun etrafında konumlanan destekleyici ve tamamlayıcı bir sistemdir. Talent14, insan kararlarını otomatikleştiren değil; insanı güçlendiren ve pedagojik süreci destekleyen yapay zekâ anlayışını benimser."
              frameworks={["UNESCO AI Ethics (Human-centered AI)", "OECD – Human-centered values", "Avrupa Konseyi Yapay Zekâ ve İnsan Hakları Yaklaşımı", "Ulusal Yapay Zekâ Stratejisi (İnsan odaklılık)"]}
              description="Bu ilke; insan onurunu, pedagojik liderliği ve nihai kararın insanda olmasını esas alan etik ve hukuki çerçevelerle uyumludur."
            />
            <PrincipleCard 
              icon={<FaChartLine />} 
              tag="1.4" 
              title="Etki Odaklılık"
              text="Talent14, yapay zekâ kullanımını çıktı miktarı veya kullanım sıklığı üzerinden değil; öğrenme sürecine sağladığı gerçek pedagojik katkı üzerinden değerlendirir. Düşünsel değer üretmeyen, pedagojik hedefle örtüşmeyen ve yalnızca niceliğe dayalı kullanımlar bilinçli olarak sınırlandırılır."
              frameworks={["OECD AI Policy Framework", "NIST AI Risk Management Framework (RMF)", "Ulusal Yapay Zekâ Stratejisi (Etki–risk dengesi)"]}
              description="Bu yaklaşım; yapay zekâ kullanımında amaç, etki ve risk dengesini gözeten yönetişim modelleriyle örtüşmektedir."
            />
            <PrincipleCard 
              icon={<FaLeaf />} 
              tag="1.5" 
              title="Kaynak Farkındalığı"
              text=""
              frameworks={["OECD Sustainable AI yaklaşımı", "UNESCO Responsible AI"]}
              description="Yapay zekânın sınırsız bir kaynak olmadığı vurgusu, sürdürülebilirlik ve sorumlu teknoloji kullanımına ilişkin küresel etik çerçevelerle uyumludur."
            />
            <PrincipleCard 
              icon={<FaChild />} 
              tag="1.6" 
              title="Yaş Duyarlılığı"
              text="Talent14, K12 düzeyinde yapay zekâ kullanımını yaş ve gelişim düzeyine göre farklılaştırır. Küçük yaş gruplarında yönlendirici ve sınırlı; orta ve üst seviyelerde ise açıklayıcı ve rehber bir yapı sunulur. Her durumda kullanım güvenli ve etik sınırlar içinde yapılandırılır."
              frameworks={["UNICEF – AI & Children", "MEB Etik Kılavuzu ve Öğrenci Güvenliği Rehberleri", "EU AI Act (Children as a protected group)"]}
              description="Çocukların yüksek yararı ve yaşa duyarlı kullanım, hem ulusal hem de uluslararası düzeyde koruyucu düzenlemelerle örtüşür."
            />
            <PrincipleCard 
              icon={<FaSearch />} 
              tag="1.7" 
              title="Tam Şeffaflık"
              text="Kullanıcılar, yapay zekânın nerede, ne zaman ve ne amaçla devrede olduğunu açıkça bilir. Arka planda çalışan, fark ettirmeden tüketim yaratan veya gizli kararlar üreten sistemler Talent14 yaklaşımının dışındadır. Şeffaflık, güvenin temel unsurudur."
              frameworks={["NIST AI RMF (Transparency & Accountability)", "ISO/IEC 42001"]}
              description="Yapay zekânın nerede ve nasıl devrede olduğunun açıkça belirtilmesi, denetlenebilir AI yönetişiminin temel şartıdır."
            />
            <PrincipleCard 
              icon={<FaGraduationCap />} 
              tag="1.8" 
              title="Öğrenme Önceliği"
              text="Yapay zekâ, öğrenme sürecinin bir parçasıdır; değerlendirme sürecinin değil. Talent14’te yapay zekâ not vermez, nihai karar almaz ve ölçme–değerlendirme yetkisini devralmaz. Değerlendirme her zaman insan sorumluluğundadır."
              frameworks={["MEB Eğitim İlkeleri", "UNESCO AI in Education Guidance"]}
              description="Yapay zekânın ölçme–değerlendirme yerine öğrenmeyi desteklemesi, pedagojik sorumluluk ilkeleriyle uyumludur."
            />
            <PrincipleCard 
              icon={<FaShieldAlt />} 
              tag="1.9" 
              title="Etik Güvence"
              text="Talent14, yapay zekâ sistemlerini güvenlik, etik ve yaş hassasiyeti katmanlarıyla koruma altına alır. Hız, ölçeklenebilirlik veya rekabet avantajı; etik ilkelerin önüne geçemez. Bu ilke, tüm AI tasarımlarının temel referans noktasıdır."
              frameworks={["UNESCO AI Ethics Framework", "OECD AI Principles", "EU AI Act (Prohibited & high-risk practices)"]}
              description="Etik ve güvenliğin hızdan önce gelmesi, hem küresel etik belgelerin hem de bağlayıcı AB mevzuatının temel yaklaşımıdır."
            />
            <PrincipleCard 
              icon={<FaSync />} 
              tag="1.10" 
              title="Yaşayan Politika (Sürekli Güncellenen Yapı)"
              text="Yapay zekâ teknolojileri ve eğitim yaklaşımları sürekli gelişmektedir. Talent14, bu değişimi izlemekle yetinmez; yönetir. Bu politika belgesi, güncellenebilir ve gelişime açık yaşayan bir kurumsal çerçeve olarak ele alınır."
              frameworks={["ISO/IEC 42001 (PDCA – Continuous Improvement)", "OECD AI Policy Lifecycle"]}
              description="Politikanın statik değil, güncellenebilir olması; sürekli iyileştirme ve yönetişim döngüsü ilkelerine dayanır."
            />
          </div>
        </section>

        <section className={style.section}>
          <div className={style.sectionHeader}>
            <h2 className={style.sectionTitle}>🏫 2. Sorumluluk ve Yönetişim</h2>
          </div>
          <div className={style.govGridHorizontal}>
            <PrincipleCard 
              icon={<FaBuilding />} 
              tag="2.1" 
              title="Kurumsal Sorumluluk ve Yönetişim"
              text="Talent14’ü kullanan kurumlar; yapay zekâ politikalarının uygulanmasından, kullanıcı rollerinin tanımlanmasından ve sistemin izlenebilir şekilde işletilmesinden sorumludur. Kurum, öğretmenleri ve öğrencileri koruyan yönetişim çerçevesini sağlar; erişim yetkileri, kullanım kayıtları ve denetim mekanizmalarını güvence altına alır."
              frameworks={["ISO/IEC 42001 – Kurumsal AI yönetişimi", "NIST AI RMF – Governance & Oversight", "KVKK rehberleri"]}
              description="Bu yapı; ISO/IEC 42001 ve NIST AI RMF’in yönetişim, gözetim ve hesap verebilirlik ilkeleriyle, KVKK’nın veri sorumlusu yaklaşımıyla uyumlu bir kurumsal sorumluluk modeli sunar."
              minimal={true}
            />
            <PrincipleCard 
              icon={<FaRobot />} 
              tag="2.2" 
              title="Yapay Zekânın Rolü ve Sınırları"
              text="Talent14 bünyesindeki yapay zekâ sistemleri; öğrenme sürecini destekleyen, açıklama üreten ve rehberlik eden yardımcı sistemlerdir. Yapay zekâ; pedagojik karar alma, ölçme‑değerlendirme veya öğrenci hakkında nihai yargı üretme yetkisine sahip değildir. Tüm çıktılar insan denetimine açıktır."
              frameworks={["EU AI Act (Risk‑based role separation)", "UNESCO AI Ethics", "OECD AI Principles"]}
              description="Bu rol tanımı; EU AI Act’in risk temelli rol ayrımı yaklaşımıyla ve UNESCO ile OECD’nin yapay zekânın destekleyici, karar verici olmayan konumuna ilişkin etik ilkeleriyle örtüşmektedir."
              minimal={true}
            />
            <PrincipleCard 
              icon={<FaSearch />} 
              tag="2.3" 
              title="Ortak Gözetim ve Hesap Verebilirlik"
              text="Talent14’te yapay zekâ kullanımı; öğretmen, kurum ve platform tarafından ortaklaşa izlenir. Kullanım kayıtları, sınır ihlalleri ve riskli senaryolar takip edilebilir yapıdadır. Bu yaklaşım, pedagojik kaliteyi korurken etik ve hukuki sorumlulukların sürdürülebilirliğini sağlar."
              frameworks={["NIST AI RMF – Accountability", "ISO/IEC 42001 – Continuous monitoring", "OECD Trustworthy AI"]}
              description="Ortak gözetim modeli; NIST AI RMF’in hesap verebilirlik ilkeleri ve ISO/IEC 42001’in sürekli izleme yaklaşımıyla uyumlu olup, güvene dayalı bir yapay zekâ yönetişimini destekler."
              minimal={true}
            />
          </div>
        </section>

        <section className={`${style.section} ${style.darkSection}`}>
          <h2 className={style.sectionTitle}>⛔ 3. Talent14 Yapay Zekâ Kullanım Sınırları</h2>
          <p className={style.sectionIntroTextDark}>
            Bu bölüm, Talent14’ün yapay zekâ kullanımına ilişkin bilinçli olarak koyduğu sınırları tanımlar. Amaç; yapay zekânın pedagojik hedeflerin önüne geçmesini, insan sorumluluğunu devralmasını veya etik riskler doğurmasını önlemektir. Bu sınırlar, Talent14’ün yapay zekâyı “her yapılabilen” değil, “yapılması doğru olan” çerçevesinde ele aldığını açıkça ortaya koyar.
          </p>
          
          <div className={style.limitsGrid}>
            <LimitItem 
              icon={<FaBan />} 
              tag="3.1" 
              title="Pedagojik Kararların Otomatikleştirilmemesi" 
              text="Talent14’te yapay zekâ; öğrenci hakkında nihai kararlar alan, yönlendirme yapan veya ölçme‑değerlendirme sonuçları üreten bir otorite olarak kullanılmaz. Öğrenme hedeflerinin belirlenmesi, öğrencinin gelişiminin değerlendirilmesi ve pedagojik kararlar her zaman insan sorumluluğundadır." 
              frameworks={["UNESCO AI Ethics (Human oversight)", "OECD AI Principles (Human responsibility)", "EU AI Act (Prohibited practices – automated decision‑making)"]}
              description="Bu sınır; UNESCO ve OECD’nin insan gözetimi ve sorumluluk ilkeleri ile EU AI Act’te yer alan, özellikle çocuklar üzerinde otomatik karar alma uygulamalarına yönelik kısıtlayıcı yaklaşım ile uyumludur."
            />
            <LimitItem 
              icon={<FaLightbulb />} 
              tag="3.2" 
              title="Öğrenci Yerine Düşünme ve Üretme" 
              text="Yapay zekâ; öğrencinin yerine düşünme, üretme veya özgün öğrenme süreçlerini ikame eden bir araç olarak konumlandırılmaz. Talent14, öğrencinin bilişsel gelişimini zayıflatacak şekilde yapay zekânın “hazır cevap üretici” olarak kullanılmasına izin vermez." 
              frameworks={["UNESCO AI in Education Guidance", "OECD Education & Skills Policy", "MEB Etik Kılavuzu"]}
              description="Bu yaklaşım; UNESCO’nun yapay zekânın öğrenmeyi destekleyici fakat ikame edici olmaması gerektiğine ilişkin rehberleri ve MEB’in öğrencinin aktif öğrenen rolünü korumaya yönelik etik ilkeleriyle örtüşmektedir."
            />
            <LimitItem 
              icon={<FaLock />} 
              tag="3.3" 
              title="Gizli, Denetlenemez ve Takipsiz Kullanım" 
              text="Talent14’te yapay zekâ; arka planda çalışan, kullanıcıdan gizlenen, denetlenemeyen veya iz bırakmayan biçimde işletilmez. Tüm yapay zekâ etkileşimleri şeffaf, izlenebilir ve gerektiğinde denetlenebilir olmak zorundadır." 
              frameworks={["NIST AI RMF (Transparency & Traceability)", "ISO/IEC 42001", "OECD Trustworthy AI"]}
              description="Bu sınır; NIST AI RMF’in şeffaflık ve izlenebilirlik ilkeleri ile ISO/IEC 42001’in denetlenebilir yapay zekâ yönetim sistemi gerekliliklerine doğrudan atıf yapmaktadır."
            />
            <LimitItem 
              icon={<FaExclamationTriangle />} 
              tag="3.4" 
              title="Yaşa Uygun Olmayan ve Riskli Senaryolar" 
              text="Talent14, öğrencilerin yaş and gelişim düzeyine uygun olmayan; psikolojik, etik veya güvenlik riski taşıyan yapay zekâ senaryolarını bilinçli olarak devre dışı bırakır. Özellikle küçük yaş gruplarında açık uçlu, kontrolsüz ve yönlendirici olmayan kullanım senaryolarına izin verilmez." 
              frameworks={["UNICEF AI & Children", "EU AI Act (Children as a protected group)", "MEB Öğrenci Güvenliği Rehberleri"]}
              description="Bu sınır; UNICEF’in çocukların yüksek yararını esas alan yapay zekâ rehberleri ile EU AI Act’te çocukların özel koruma grubu olarak tanımlanmasına dayanan risk temelli yaklaşım ile uyumludur."
            />
          </div>

          <div className={style.noList}>
            <h3>🧾 ÖZETLE – Bilinçli Olarak Yapmadıklarımız</h3>
            <p className={style.noListSubtitle}>🚫 Net Sınırlar – Açık Duruş</p>
            <ul>
              <li>❌ Yapay zekâyı öğretmenin yerine konumlandırmayız.</li>
              <li>❌ Sınırsız ve denetimsiz kullanım sunmayız.</li>
              <li>❌ Öğrencinin yerine düşünen veya üreten sistemler kurmayız.</li>
              <li>❌ Akademik dürüstlüğü zedeleyen kullanımlara izin vermeyiz.</li>
              <li>❌ Yaş ve gelişim hassasiyetlerini göz ardı etmeyiz.</li>
              <li>❌ Yapay zekâya değerlendirme ve nihai karar yetkisi vermeyiz.</li>
              <li>❌ Şeffaf olmayan AI süreçleri yürütmeyiz.</li>
              <li>❌ Yapay zekâyı bedelsiz ve sınırsız bir kaynak gibi sunmayız.</li>
              <li>❌ Kurumsal iradeyi baypas eden kullanım senaryoları oluşturmayız.</li>
            </ul>
          </div>
        </section>

        <section className={style.section}>
          <div className={style.sectionHeader}>
            <h2 className={style.sectionTitle}>📜 4. Kurumlara Açık Yapay Zekâ Deklarasyonu</h2>
            <p className={style.sectionIntroText}>
              Bu bölüm, Talent14’ün yapay zekâ kullanımına ilişkin kurumlara verdiği açık taahhütleri ve kamusal duruşunu ortaya koyar. Bu deklarasyon; iş ortakları, eğitim kurumları, veliler ve tüm paydaşlar için Talent14’ün yapay zekâyı nasıl konumlandırdığını net ve şeffaf biçimde ifade eder. Talent14, yapay zekâ kullanımını gizli sözleşmelerin arkasına saklamaz; açık, denetlenebilir ve sorumluluk sahibi bir yaklaşımı esas alır.
            </p>
          </div>
          
          <div className={style.declGrid}>
            <div className={style.declCard}>
              <FaHandshake className={style.declIcon} />
              <h3>4.1 Kurumlara Açık Taahhüt</h3>
              <p>Talent14, yapay zekâyı kullandığı tüm eğitim ortamlarında; pedagojik liderliğin öğretmende kaldığını, insan denetiminin esas olduğunu ve öğrencinin gelişim bütünlüğünün önceliklendirildiğini açıkça taahhüt eder.</p>
              <div className={style.cardMeta}>
                <span className={style.fw}>Dayanılan Çerçeveler: UNESCO Ethics of Artificial Intelligence, OECD AI Principles, EU AI Act (Transparency obligations)</span>
                <p className={style.cardDesc}>Açıklama: Bu taahhüt; UNESCO ve OECD’nin şeffaflık ve insan merkezlilik ilkeleri ile EU AI Act’te yer alan açık bilgilendirme ve sorumluluk gereklilikleriyle uyumludur.</p>
              </div>
            </div>
            <div className={style.declCard}>
              <FaBullhorn className={style.declIcon} />
              <h3>4.2 Şeffaflık ve Hesap Verebilirlik Sözü</h3>
              <p>Talent14, yapay zekâ sistemlerinin nasıl çalıştığını, hangi sınırlar içinde kullanıldığını ve hangi durumlarda devre dışı bırakıldığını kurumlara açık biçimde sunar. Kullanım sınırları, yetki dağılımları ve denetim mekanizmaları gizlenmez.</p>
              <div className={style.cardMeta}>
                <span className={style.fw}>Dayanılan Çerçeveler: NIST AI Risk Management Framework, ISO/IEC 42001, OECD Trustworthy AI</span>
                <p className={style.cardDesc}>Açıklama: Bu yaklaşım; NIST AI RMF’in hesap verebilirlik ve izlenebilirlik ilkeleri ile ISO/IEC 42001’in denetlenebilir yapay zekâ yönetişimi yaklaşımıyla örtüşmektedir.</p>
              </div>
            </div>
            <div className={style.declCard}>
              <FaBuilding className={style.declIcon} />
              <h3>4.3 Kurumsal Uyum ve İş Birliği</h3>
              <p>Talent14, kurumların kendi iç politika, mevzuat ve etik çerçeveleriyle uyumlu bir şekilde çalışmayı esas alır. Gerekli durumlarda kurumlara özel yapılandırmalar, erişim sınırları ve raporlama mekanizmaları sunar.</p>
              <div className={style.cardMeta}>
                <span className={style.fw}>Dayanılan Çerçeveler: ISO/IEC 42001, OECD AI Governance, Ulusal ve kurumsal uyum rehberleri</span>
                <p className={style.cardDesc}>Açıklama: Bu madde; kurumsal yapılarla birlikte çalışabilir, uyarlanabilir ve yönetişim temelli yapay zekâ sistemleri oluşturma hedefiyle uyumludur.</p>
              </div>
            </div>
            <div className={style.declCard}>
              <FaFileAlt className={style.declIcon} />
              <h3>4.4 Açık Deklarasyon</h3>
              <p>Talent14, yapay zekâyı eğitimde rekabet avantajı sağlayan gizli bir araç olarak değil; etik, pedagojik ve toplumsal sorumluluk çerçevesinde açıkça beyan edilen bir sistem olarak konumlandırır. Bu politika belgesi, kurumlara verilen sözün yazılı ve denetlenebilir karşılığıdır.</p>
              <div className={style.cardMeta}>
                <span className={style.fw}>Dayanılan Çerçeveler: UNESCO AI Ethics, OECD AI Principles, Avrupa Konseyi İnsan Hakları ve Yapay Zekâ Yaklaşımı</span>
                <p className={style.cardDesc}>Açıklama: Bu deklarasyon; yapay zekâ kullanımında kamusal güvenin tesis edilmesini esas alan uluslararası etik ve insan hakları temelli yaklaşımlarla uyumludur.</p>
              </div>
            </div>
          </div>
          
          <div className={style.finalSummary}>
            <h3>🧾 ÖZETLE – Kurumsal Taahhüt Maddeleri</h3>
            <div className={style.checkGrid}>
              <span><FaCheckCircle /> Eğitimin merkezinde insan vardır.</span>
              <span><FaCheckCircle /> Kurumsal irade esastır.</span>
              <span><FaCheckCircle /> Yapay zekâ yetki sahibi değil, yetkiyle çalışan bir araçtır.</span>
              <span><FaCheckCircle /> Sınırsız değil, kontrollü kullanım benimsenir.</span>
              <span><FaCheckCircle /> Yaş gruplarına duyarlı, güvenli ve etik yaklaşım temel alınır.</span>
              <span><FaCheckCircle /> Güvenlik, etik ve şeffaflık vazgeçilmezdir.</span>
              <span><FaCheckCircle /> Yapay zekâ bir kaynak olarak ele alınır.</span>
              <span><FaCheckCircle /> Sürekli gelişim ve iş birliği taahhüt edilir.</span>
            </div>
          </div>
        </section>

        <footer className={style.pageFooter}>
          <div className={style.appendix}>
            <h4>EKLER</h4>
            <div className={style.appendixLinks}>
              <Link to="/ai-policy/uygulama-plani" className={style.appendixLinkItem}>
                <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>EK–1: Uygulama Planı</motion.span>
              </Link>
              <Link to="/ai-policy/maarif-modeli-uyumu" className={style.appendixLinkItem}>
                <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>EK–2: Maarif Modeli Uyumu</motion.span>
              </Link>
              <Link to="/ai-policy/kullanici-taahhutnamesi" className={style.appendixLinkItem}>
                <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>EK–3: Kullanıcı Taahhüdü</motion.span>
              </Link>
              <Link to="/ai-policy/kredi-sistemi" className={style.appendixLinkItem}>
                <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>EK–4: Kredi Sistemi</motion.span>
              </Link>
              <Link to="/ai-policy/referans-dokumanlar" className={style.appendixLinkItem}>
                <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>EK–5: Referans Bilgiler</motion.span>
              </Link>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

const PrincipleCard = ({ icon, tag, title, text, frameworks, description, minimal }) => (
  <motion.div 
    whileHover={{ x: 10 }}
    className={`${style.principleCard} ${minimal ? style.minimal : ''}`}
  >
    <div className={style.pHead}>
      <span className={style.pIcon}>{icon}</span>
    </div>
    <div className={style.pMainContent}>
      <div className={style.pTitleArea}>
        <h3>{title}</h3>
        <span className={style.pTag}>{tag}</span>
      </div>
      {text && <p className={style.pText}>{text}</p>}
      <div className={style.pMetaRow}>
        <div className={style.pFrameworks}>
          <strong>Çerçeveler</strong>
          <div className={style.fwList}>
            {frameworks.map((f, i) => <span key={i}>{f}</span>)}
          </div>
        </div>
        {description && (
          <div className={style.pDescription}>
            <strong>Açıklama</strong>
            <p>{description}</p>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

const LimitItem = ({ icon, tag, title, text, frameworks, description }) => (
  <div className={style.limitItemContainer}>
    <div className={style.limitItem}>
      <div className={style.lIcon}>{icon}</div>
      <div className={style.lContent}>
        <div className={style.lHead}>
          <span className={style.lTag}>{tag}</span>
          <h4>{title}</h4>
        </div>
        <p className={style.lMainText}>{text}</p>
        <div className={style.lMeta}>
          <div className={style.lFrameworks}>
            <strong>Dayanılan Çerçeveler:</strong> {frameworks.join(', ')}
          </div>
          <div className={style.lDesc}>
            <strong>Açıklama:</strong> {description}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default AIPolicyPage;
